<script lang="ts" setup>
import { AxiosError } from 'axios'
import { ref } from 'vue'
import { AlbumSummary } from '../../../libs/models'
import { useApi } from '../libs/api'
import illustUrl from '../libs/illustUrl'
import { useNotificationStore } from '../libs/notificationStore'
import { getApiErrorMessage } from '../libs/apiErrors'
import { l } from '../libs/localization'
import Loading from './Loading.vue'
import Modal from './Modal.vue'

const props = defineProps<{ illustId: number }>()
const emit = defineEmits(['close'])

const api = useApi()
const notificationStore = useNotificationStore()
const albums = ref<AlbumSummary[]>([])
const isLoading = ref(true)
const addingAlbumId = ref<number | null>(null)

api.get<AlbumSummary[]>('/albums').then(({ data }) => {
  albums.value = data
}).catch(error => {
  alert(getApiErrorMessage(error, 'アルバムを取得できませんでした'))
  emit('close')
}).finally(() => {
  isLoading.value = false
})

const addToAlbum = (album: AlbumSummary) => {
  if (addingAlbumId.value !== null) return
  addingAlbumId.value = album.id
  api.post(`/albums/${album.id}/illusts`, { illustId: props.illustId }).then(() => {
    notificationStore.push(l('「{name}」に追加しました', { name: album.name }), 'success')
    emit('close')
  }).catch(error => {
    const apiError = error as AxiosError<{ error?: string }>
    if (apiError.response?.status === 409) {
      notificationStore.push(l('既に追加済みです'))
      emit('close')
      return
    }
    alert(getApiErrorMessage(error, 'アルバムに追加できませんでした'))
  }).finally(() => {
    addingAlbumId.value = null
  })
}
</script>

<template>
  <Modal @close="$emit('close')">
    <div class="ModalContent AlbumPickerContent">
      <div class="ModalTitle">{{ l('アルバムへ追加') }}</div>
      <Loading v-if="isLoading" class="AlbumsLoading" />
      <div v-if="!isLoading && !albums.length" class="EmptyState">{{ l('アルバムがまだありません') }}</div>
      <ul v-if="!isLoading && albums.length" class="PickerList">
        <li v-for="album in albums" :key="album.id">
          <button class="PickerRow" :class="{ disabled: addingAlbumId !== null }" :disabled="addingAlbumId !== null" @click="addToAlbum(album)">
            <span class="AlbumCover">
              <img v-if="album.cover_illust" :src="illustUrl(album.cover_illust.user_id, album.cover_illust.id)" alt="">
              <span v-else class="EmptyCover"><span class="material-symbols-rounded" aria-hidden="true">photo_library</span></span>
            </span>
            <span class="CardInfo">
              <span class="AlbumName">{{ album.name }}</span>
              <span class="AlbumVisibility" :class="{ private: album.private }">
                <span class="material-symbols-rounded" aria-hidden="true">{{ album.private ? 'lock' : 'public' }}</span>
                {{ album.private ? l('非公開') : l('公開') }}
              </span>
            </span>
            <span class="PickAction" :aria-label="l('追加')"><span class="material-symbols-rounded" aria-hidden="true">add</span></span>
          </button>
        </li>
      </ul>
      <button class="cancel" @click="$emit('close')">{{ l('閉じる') }}</button>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
.AlbumPickerContent {
  width: 600px;
  max-width: calc(100vw - 30px);
  line-height: 1.5;
  > button { margin-top: 10px; }
}
.AlbumsLoading { margin: 40px auto; }
.EmptyState { padding: 40px 10px; color: var(--tx-color-light); font-size: 13px; text-align: center; }
.PickerList { margin: 16px 0 24px; list-style: none; }
button.PickerRow {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  border: 0;
  border-bottom: 1px dashed var(--border-color);
  border-radius: 0;
  padding: 12px 0;
  background: transparent;
  box-shadow: none;
  color: var(--tx-color);
  font-weight: normal;
  text-align: left;
  text-shadow: none;
}
.AlbumCover {
  overflow: hidden;
  width: 80px;
  flex-shrink: 0;
  aspect-ratio: 4 / 3;
  border-radius: 10px;
  img { display: block; width: 100%; height: 100%; object-fit: cover; }
}
.EmptyCover {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  aspect-ratio: 4 / 3;
  background: var(--border-color);
  color: var(--tx-color-light);
  .material-symbols-rounded { font-size: 32px; }
}
.CardInfo { flex: 1; min-width: 0; }
.AlbumName { display: block; font-size: 13px; font-weight: bold; overflow-wrap: anywhere; }
.AlbumVisibility {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  color: var(--green);
  font-size: 11px;
  &.private { color: var(--tx-color-light); }
}
.AlbumVisibility .material-symbols-rounded { font-size: 16px; }
.PickAction {
  color: var(--tx-color);
  .material-symbols-rounded { margin: 0; font-size: 22px; }
}
</style>
